import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Card, Form, Alert } from "react-bootstrap";
import { FaPlus, FaTrash, FaPhone } from "react-icons/fa";
import { createSite, updateSite } from "../../services/customerService";
import "./SiteForm.css";

/**
 * SiteForm
 * - Full-width layout with proper spacing
 * - Contact management with phone numbers
 */

const emptyContact = () => ({
  name: "",
  email: "",
  phone_numbers: [{ type: "primary", number: "" }],
});

const SiteForm = ({ customerId, siteToEdit, onFormSubmit, onCancel }) => {
  const isEdit = !!siteToEdit?.id;

  const initialState = useMemo(() => {
    return {
      name: siteToEdit?.name ?? "",
      address: siteToEdit?.address ?? "",
      lockbox_location: siteToEdit?.lockbox_location ?? "",
      contacts:
        siteToEdit?.contacts?.length > 0
          ? siteToEdit.contacts.map((c) => ({
              name: c?.name ?? "",
              email: c?.email ?? "",
              phone_numbers: Array.isArray(c?.phone_numbers) && c.phone_numbers.length > 0
                ? c.phone_numbers.map((p) => ({
                    type: p?.type ?? "primary",
                    number: p?.number ?? "",
                  }))
                : [{ type: "primary", number: "" }],
            }))
          : [],
    };
  }, [siteToEdit]);

  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => setForm(initialState), [initialState]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateContact = (idx, patch) => {
    setForm((prev) => {
      const next = [...prev.contacts];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, contacts: next };
    });
  };

  const addContact = () =>
    setForm((prev) => ({ ...prev, contacts: [...prev.contacts, emptyContact()] }));

  const removeContact = (idx) =>
    setForm((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== idx),
    }));

  const addPhone = (contactIdx) => {
    setForm((prev) => {
      const contacts = [...prev.contacts];
      const c = contacts[contactIdx];
      contacts[contactIdx] = {
        ...c,
        phone_numbers: [...(c.phone_numbers ?? []), { type: "mobile", number: "" }],
      };
      return { ...prev, contacts };
    });
  };

  const removePhone = (contactIdx, phoneIdx) => {
    setForm((prev) => {
      const contacts = [...prev.contacts];
      const c = contacts[contactIdx];
      const phone_numbers = (c.phone_numbers ?? []).filter((_, i) => i !== phoneIdx);
      contacts[contactIdx] = { ...c, phone_numbers: phone_numbers.length ? phone_numbers : [{ type: "primary", number: "" }] };
      return { ...prev, contacts };
    });
  };

  const updatePhone = (contactIdx, phoneIdx, patch) => {
    setForm((prev) => {
      const contacts = [...prev.contacts];
      const c = contacts[contactIdx];
      const phone_numbers = [...(c.phone_numbers ?? [])];
      phone_numbers[phoneIdx] = { ...phone_numbers[phoneIdx], ...patch };
      contacts[contactIdx] = { ...c, phone_numbers };
      return { ...prev, contacts };
    });
  };

  const validate = () => {
    if (!form.name.trim()) return "Site name is required.";
    if (!form.address.trim()) return "Address is required.";
    return "";
  };

  const saveSite = async (payload) => {
    if (isEdit) {
      return await updateSite(siteToEdit.id, payload);
    }
    return await createSite(customerId, payload);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    const payload = {
      customer_id: customerId,
      name: form.name.trim(),
      address: form.address.trim(),
      lockbox_location: form.lockbox_location.trim(),
      contacts: form.contacts.map((c) => ({
        name: c.name?.trim(),
        email: c.email?.trim(),
        phone_numbers: (c.phone_numbers ?? []).map((p) => ({
          type: p.type,
          number: p.number?.trim(),
        })),
      })),
    };

    try {
      setSaving(true);
      const saved = await saveSite(payload);
      onFormSubmit?.(saved);
    } catch (err) {
      setError(err?.message || "Failed to save site.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="site-form">
      <Form onSubmit={handleSubmit}>
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Site Information */}
        <Row className="g-3 mb-3">
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>Site Name *</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Main Office"
              />
              <Form.Text className="text-muted">A descriptive name to identify this location</Form.Text>
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>Address *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.address}
                onChange={(e) => setField("address", e.target.value)}
                placeholder="Street, City, State, Zip"
              />
            </Form.Group>
          </Col>

          <Col xs={12}>
            <Form.Group>
              <Form.Label>Lockbox Location</Form.Label>
              <Form.Control
                value={form.lockbox_location}
                onChange={(e) => setField("lockbox_location", e.target.value)}
                placeholder="Where to find keys/access (e.g., front door, gate)"
              />
              <Form.Text className="text-muted">Instructions for accessing the site</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {/* Contacts Section */}
        <div className="contact-manager mt-4">
          <div className="cm-header mb-3">
            <h5 className="mb-0">Site Contacts</h5>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={addContact}
            >
              <FaPlus className="me-1" /> Add Contact
            </Button>
          </div>

          {form.contacts.length === 0 ? (
            <div className="text-center text-muted py-4 border rounded">
              No contacts yet. Click <strong>Add Contact</strong> to add one.
            </div>
          ) : (
            <div className="cm-list">
              {form.contacts.map((contact, idx) => (
                <Card key={idx} className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center bg-light">
                    <h6 className="mb-0">Contact #{idx + 1}</h6>
                    <div className="d-flex gap-2">
                      <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => addPhone(idx)}
                      >
                        <FaPhone className="me-1" /> Add Phone
                      </Button>
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeContact(idx)}
                      >
                        <FaTrash className="me-1" /> Remove
                      </Button>
                    </div>
                  </Card.Header>

                  <Card.Body className="p-3">
                    <Row className="g-3">
                      {/* Contact Info Section - Left Column */}
                      <Col xs={12} md={7}>
                        <div className="contact-info-section p-3 border rounded" style={{ minHeight: '200px' }}>
                          <h6 className="mb-3 fw-bold">Contact Info</h6>
                          <Form.Group className="mb-3">
                            <Form.Label className="small fw-semibold">Name *</Form.Label>
                            <Form.Control
                              value={contact.name}
                              onChange={(e) => updateContact(idx, { name: e.target.value })}
                              placeholder="Michael Loera"
                              size="sm"
                            />
                          </Form.Group>

                          <Form.Group>
                            <Form.Label className="small fw-semibold">Email</Form.Label>
                            <Form.Control
                              type="email"
                              value={contact.email}
                              onChange={(e) => updateContact(idx, { email: e.target.value })}
                              placeholder="contact@email.com"
                              size="sm"
                            />
                          </Form.Group>
                        </div>
                      </Col>

                      {/* Phone Numbers Section - Right Column */}
                      <Col xs={12} md={5}>
                        <div className="phone-numbers-section p-3 border rounded d-flex flex-column" style={{ minHeight: '120px' }}>
                          <div className="mb-0">
                            <h6 className="mb-0 fw-bold">Phone Numbers</h6>
                          </div>

                          {contact.phone_numbers && contact.phone_numbers.length > 0 && (
                            <div className="phone-list flex-grow-1 d-flex flex-column justify-content-center">
                              {contact.phone_numbers.map((phone, pIdx) => (
                                <Row key={pIdx} className="g-2 mb-2 align-items-center">
                                  <Col xs={4}>
                                    <Form.Select
                                      value={phone.type}
                                      onChange={(e) => updatePhone(idx, pIdx, { type: e.target.value })}
                                      size="sm"
                                    >
                                      <option value="primary">Primary</option>
                                      <option value="mobile">Mobile</option>
                                      <option value="office">Office</option>
                                      <option value="home">Home</option>
                                      <option value="other">Other</option>
                                    </Form.Select>
                                  </Col>
                                  <Col xs={6} className="pe-3">
                                    <Form.Control
                                      type="tel"
                                      value={phone.number}
                                      onChange={(e) => updatePhone(idx, pIdx, { number: e.target.value })}
                                      placeholder="(555) 123-4567"
                                      size="sm"
                                    />
                                  </Col>
                                  <Col xs={2} className="d-flex justify-content-center">
                                    <Button
                                      type="button"
                                      variant="danger"
                                      size="sm"
                                      onClick={() => removePhone(idx, pIdx)}
                                      className="d-flex align-items-center justify-content-center ms-3"
                                      style={{
                                        width: '40px',
                                        height: '38px',
                                        padding: '0',
                                        minWidth: '40px'
                                      }}
                                      title="Remove phone"
                                    >
                                      <FaTrash />
                                    </Button>
                                  </Col>
                                </Row>
                              ))}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
          <div className="text-muted small">
            {siteToEdit?.customer_name && (
              <>
                <strong>Customer:</strong> {siteToEdit.customer_name}
              </>
            )}
          </div>
          <div>
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={saving}
              className="me-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
            >
              {saving ? "Saving..." : isEdit ? "Update Site" : "Create Site"}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default SiteForm;
